import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsList, TabsTrigger } from '../ui';
import { XIcon } from '../ui/Icons';
import { splitViewTabCountBadgeClass } from './splitViewFieldDisplay';

/** Tab list: all labels visible; wraps to a second line when narrow. */
export const SPLIT_VIEW_TABS_LIST_CLASS =
  'flex w-full flex-wrap gap-1 bg-transparent justify-start h-auto p-0 pb-1 border-0';

export const SPLIT_VIEW_TABS_TRIGGER_CLASS =
  'flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 data-[state=active]:bg-gray-100 dark:data-[state=active]:bg-gray-800 data-[state=active]:text-gray-900 dark:data-[state=active]:text-gray-100 data-[state=active]:font-semibold rounded-lg transition-all duration-200 shrink-0';

/**
 * Wrapping tab bar for split-view panels (used by GenericFormsPage custom split views).
 * @param {{ value: string, label: string, icon?: React.ReactNode, badge?: number|null, badgeColor?: string }[]} tabs
 */
export function SplitViewTabBar({
  tabs = [],
  wrapperClassName,
  listClassName,
  triggerClassName,
}) {
  const { theme } = useTheme();

  return (
    <div
      className={cn(
        'w-full border-b border-gray-200 dark:border-gray-700',
        theme.border.light,
        wrapperClassName,
      )}
    >
      <TabsList className={cn(SPLIT_VIEW_TABS_LIST_CLASS, listClassName)}>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className={cn(SPLIT_VIEW_TABS_TRIGGER_CLASS, triggerClassName)}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {tab.badge > 0 && (
              <span className={splitViewTabCountBadgeClass()}>{tab.badge}</span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </div>
  );
}

/**
 * Standard shell for custom split-view panels opened from GenericFormsPage (view icon).
 */
export function CustomSplitViewWithTabs({
  isOpen,
  config,
  labels = {},
  headerTitle,
  selectedRecord,
  emptyMessage,
  defaultTab = 'details',
  tabs = [],
  children,
  onClose,
}) {
  const { theme } = useTheme();

  if (!isOpen) return null;

  const title =
    headerTitle ??
    (selectedRecord
      ? labels.viewTitle || 'Record details'
      : labels.noSelectionText || 'Select a row to view details');

  return (
    <div
      className={cn('hidden lg:block min-h-0 h-full pb-2')}
      style={{ width: config?.ui?.drawerWidth || '420px' }}
    >
      <div
        className={cn(
          'min-h-0 h-full rounded-xl border flex flex-col',
          theme.border.light,
          theme.colors.surface,
          theme.shadow.md
        )}
      >
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn('text-sm font-semibold', theme.text.primary)}>{title}</div>
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1 rounded hover:opacity-80', theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {selectedRecord ? (
          <Tabs
            defaultValue={defaultTab}
            className="flex-1 flex flex-col min-h-0 px-3 py-2"
          >
            <SplitViewTabBar tabs={tabs} />
            <div className="flex-1 flex flex-col min-h-0">{children}</div>
          </Tabs>
        ) : (
          <div className={cn('p-4 text-sm', theme.text.secondary)}>
            {emptyMessage || labels.noSelectionText || 'No record selected'}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomSplitViewWithTabs;
